import React, {useState} from 'react';
import {Checkbox, Icon, IconButton, Spinner, SpinnerSize, TooltipHost} from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from '../Storefront/MarkupTypeSelection';

import {
    useGetGlobalStorefrontSettingsQuery, useGetStorefrontBundleEstimateMutation,
    useGetStorefrontQuery,
} from "../../store/endpoints/StorefrontSettingsEndpoints";
import {IIconProps} from "@fluentui/react/lib-amd/Icon";
import StorefrontDetailsPanel from "../Storefront/StorefrontDetailsPanel";
import {debounce} from "lodash";
import {IProductBundleItem} from "../../models/ProductBundles";
import {getCurrencySymbol} from "../../logic/currencyLocalizationHelper";


interface IBundleStorefrontEntryDetailsProps {
    storefrontId?: string;
    storefrontName?: string
    bundleId: string;
    included?: boolean;
    onIncludedChange?: (included: boolean) => void;
    onMarkupChange: (markup?: number) => void;
    onMarkupTypeChange: (markupType: string) => void;
    onMinimumPriceChange: (minimumPrice?: number) => void;
    includeAllStorefronts?: boolean;
    markupType?: string;
    markup?: number;
    minimumPrice?: number;
    items?: IProductBundleItem[];
}

const BundleStorefrontEntryDetails: React.FC<IBundleStorefrontEntryDetailsProps> = (props) => {
    
    const [detailsPanelOpen, setDetailsPanelOpen] = useState<boolean>(false);
    const storefront = useGetStorefrontQuery(props.storefrontId ?? '', {skip: !props.storefrontId || props.storefrontId === ''});
    const storefrontGlobalSettings = useGetGlobalStorefrontSettingsQuery();
    
    const markupChange = (markup?: number) => {
        if (!props.storefrontId) return;
        props.onMarkupChange(markup);
    }
    const minimumPriceChange = (minimumPrice?: number) => {
        if (!props.storefrontId) return;
        props.onMinimumPriceChange(minimumPrice);
    }
    const markupTypeChange = (markupType: string) => {
        if (!props.storefrontId) return;
        props.onMarkupTypeChange(markupType);
    }
    const onIncludedChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
        if (props.onIncludedChange) props.onIncludedChange(checked ?? false);
    }
    
    if(!props.storefrontId || !props.storefrontName){
        return <></>
    }
    const getIncludedState =() => {
        if (props.includeAllStorefronts) {
            return storefront?.data?.ignoreInheritedProducts ? props.included : true;
        }
        return props.included ?? false;
    }

    const editIcon: IIconProps = { iconName: 'edit' };
    
    
    const getMarkupPlaceholder = () => {
        if(storefront?.data?.defaultMarkup){
            return `${storefront.data.defaultMarkup} - from Storefront`;
        }
        if(storefrontGlobalSettings?.data?.defaultMarkup) {
            return `${storefrontGlobalSettings.data.defaultMarkup} - from Global`;
        }
        return '0 - Not Set';
    }
    return (
        <tr style={{verticalAlign: 'middle'}}>
            <StorefrontDetailsPanel storefrontId={props.storefrontId} globalSettings={storefrontGlobalSettings?.data} isOpen={detailsPanelOpen} onClose={() => setDetailsPanelOpen(false)}/>
            <td>
                <IconButton iconProps={editIcon} onClick={() => setDetailsPanelOpen(true)} />
            </td>
            <td style={{minWidth: '200px'}}>{props.storefrontName}</td>
            <td>
                <Checkbox
                    disabled={props.includeAllStorefronts}
                    checked={getIncludedState()}
                    onChange={onIncludedChange}
                />
            </td>
            <td>
                <NumberField
                    placeholder={getMarkupPlaceholder()}
                    value={props.markup}
                    onChange={markupChange}
                    decimal={true}
                />
            </td>
            <td>
                <NumberField 
                    prefix={getCurrencySymbol(storefrontGlobalSettings.data?.currency ?? 'USD')}
                    decimal={true} 
                    value={props.minimumPrice} 
                    onChange={minimumPriceChange} 
                />
                
            </td>
            <td>
                <MarkupTypeSelection
                    storefrontId={props.storefrontId}
                    value={props.markupType}
                    onChange={markupTypeChange}
                    bundleContext={true}
                />
            </td>
            <td>
                <BundleStorefrontPriceEstimate
                    included={getIncludedState()}
                    markup={props.markup}
                    markupType={props.markupType ?? ''}
                    workspaceId={props.bundleId}
                    minimumPrice={props.minimumPrice}
                    storefrontId={props.storefrontId ?? ''}
                    items={props.items}
                />
            </td>
        </tr>
    );
}

interface IBundleStorefrontPriceEstimateProps {
    included?: boolean;
    markup?: number;
    markupType?: string;
    workspaceId: string;
    minimumPrice?: number;
    storefrontId: string;
    items?: IProductBundleItem[];
}

const BundleStorefrontPriceEstimate: React.FC<IBundleStorefrontPriceEstimateProps> = (props) => {
    // Component logic here
    const [estimator] = useGetStorefrontBundleEstimateMutation();
    const [estimate, setEstimate] = React.useState<number | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [messages, setMessages] = React.useState<{[key:string]: string}>({});

    React.useEffect(() => {
        debouncedEstimator(props.workspaceId, props.included, props.markup, props.markupType, props.minimumPrice, props.storefrontId);
    }, [props.workspaceId, props.included, props.markup, props.markupType, props.minimumPrice, props.storefrontId]);

    const debouncedEstimator = React.useCallback(
        debounce((workspaceId, included, markup, markupType, minimumPrice, storefrontId) => {
            if (included && workspaceId) {
                setLoading(true);
                estimator({
                    storefrontId: storefrontId,
                    bundleId: workspaceId,
                    minimumPrice: minimumPrice,
                    markupType: markupType,
                    markup: markup,
                })
                .unwrap()
                .then((response) => {
                    setEstimate(response.price);
                    setMessages(response.messages ?? {});
                })
                .catch((error) => {
                    console.error('Error estimating bundle:', error);
                })
                .finally(() => {
                    setLoading(false);
                });
            }
        }, 500, { leading: false }),
    []
    );

    const getCurrencyValue = (value?: number, currency?: string) => {
        const checkCurrency = (currency && currency.length > 0) ? currency : 'USD';
        try {
            return `${new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: checkCurrency
            }).format(value ?? 0)}`
        } catch (error) {
            console.error('Error formatting currency:', error);
            return `$${value ?? 0}`;
        }
    }

    if (loading) return <Spinner size={SpinnerSize.small} label="Calculating..." />;
    if (!props.included) return <span style={{color: 'lightgrey'}}>Enable to calculate price</span>;
    if (estimate === undefined) return <span>Price estimate not available <BundleStorefrontMessages messages={messages} items={props.items} /></span>;

    return <React.Fragment>
        {getCurrencyValue(estimate)}
        <BundleStorefrontMessages messages={messages} items={props.items} />
    </React.Fragment>
}

interface IBundleStorefrontMessagesProps {
    messages: {[key:string]: string};
    items?: IProductBundleItem[];
}

const BundleStorefrontMessages: React.FC<IBundleStorefrontMessagesProps> = (props) => {
    if (!props.messages || Object.keys(props.messages).length === 0) return null;

    const content = (
        <React.Fragment>
            {Object.entries(props.messages).map(([key, message]) => {
                const item = props.items?.find(i => i.productId === key);
                return (
                    <div key={key} style={{fontSize: '90%' }}>
                        {item?.sourceItem?.name ? <strong>{item.sourceItem.name}: </strong> : null}{message}
                    </div>
                )}
            )}
        </React.Fragment>
    );
    return (<TooltipHost
        content={content}
        calloutProps={{ gapSpace: 0 }}
        styles={{ root: { display: 'inline-block', marginLeft: '8px' } }}
    >
        <div>
            <Icon iconName='Warning' style={{ color: '#ff0000', cursor: 'pointer' }} />
        </div>
    </TooltipHost>
    )
}

export default BundleStorefrontEntryDetails;